import { useState, useEffect, useCallback } from 'react';
import { userService } from '../services/service';

export const useUsers = (initialParams = {}) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const fetchUsers = useCallback(async (params = {}) => {
        try {
            setLoading(true);
            setError(null);

            const queryParams = {
                page: currentPage,
                per_page: pageSize,
                search: searchTerm,
                role: roleFilter !== 'all' ? roleFilter : undefined,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                ...initialParams,
                ...params,
            };

            const response = await userService.getAllUsersWithRoles(queryParams);
            setUsers(response?.data || []);
            setPagination(response);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchTerm, roleFilter, statusFilter, initialParams]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const createUser = async (data) => {
        try {
            setLoading(true);
            const response = await userService.create(data);
            await fetchUsers();
            return response;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const updateUser = async (id, data) => {
        try {
            setLoading(true);
            const response = await userService.update(id, data);
            await fetchUsers();
            return response;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const activateUser = async (id) => {
        try {
            setLoading(true);
            const response = await userService.activate(id);
            await fetchUsers();
            return response;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const deactivateUser = async (id) => {
        try {
            setLoading(true);
            const response = await userService.deactivate(id);
            await fetchUsers();
            return response;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const activateUserLogin = async (id) => {
        try {
            setLoading(true);
            const response = await userService.activateLogin(id);
            await fetchUsers();
            return response;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const deactivateUserLogin = async (id) => {
        try {
            setLoading(true);
            const response = await userService.deactivateLogin(id);
            await fetchUsers();
            return response;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const activateUserBoth = async (id) => {
        try {
            setLoading(true);
            const response = await userService.activateBoth(id);
            await fetchUsers();
            return response;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const deactivateUserBoth = async (id) => {
        try {
            setLoading(true);
            const response = await userService.deactivateBoth(id);
            await fetchUsers();
            return response;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const changeUserRole = async (id, role) => {
        try {
            setLoading(true);
            const response = await userService.changeRole(id, role);
            await fetchUsers();
            return response;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const changeUserPassword = async (id, newPassword) => {
        try {
            setLoading(true);
            const response = await userService.changeUserPassword(
                id,
                newPassword,
            );
            return response;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const getUserPermissions = async (id) => {
        try {
            const response = await userService.getPermissions(id);
            return response;
        } catch (err) {
            setError(err);
            throw err;
        }
    };

    const getUserStats = async () => {
        try {
            const stats = await userService.getStats();
            return stats;
        } catch (err) {
            setError(err);
            throw err;
        }
    };

    return {
        users,
        loading,
        error,
        pagination,
        searchTerm,
        setSearchTerm,
        roleFilter,
        setRoleFilter,
        statusFilter,
        setStatusFilter,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        fetchUsers,
        createUser,
        updateUser,
        activateUser,
        deactivateUser,
        activateUserLogin,
        deactivateUserLogin,
        activateUserBoth,
        deactivateUserBoth,
        changeUserRole,
        changeUserPassword,
        getUserPermissions,
        getUserStats,
    };
};